import { useEffect, useState } from 'react'
import { AnimatePresence, motion } from 'motion/react'
import {
  Sparkles, ChevronRight, Check, Loader2, X as XIcon,
  Globe, Hash, LineChart, Activity, Building2, Search,
  Download, Code2, SquareTerminal, FolderOpen, FileText, Link2,
  ArrowLeftRight, Wallet, CalendarClock, BrainCog, FolderSearch, Send,
  Calculator, Database, Layers, Workflow, Eye, Radar,
  type LucideIcon,
} from 'lucide-react'
import type { ChatMessage, ToolEvent, Citation } from '@shared/types'
import { Markdown } from './Markdown'
import { useStore } from '../../store'
import { cn } from '../../lib/utils'

function domainOf(url: string): string {
  try {
    return new URL(url).hostname.replace(/^www\./, '')
  } catch {
    return url
  }
}
/** xAI search citations often have a numeric title ("1", "2") — fall back to the domain. */
function citeTitle(c: Citation): string {
  const t = (c.title ?? '').trim()
  return t && !/^\d+$/.test(t) ? t : domainOf(c.url)
}

/* ── per-tool icon + verb registry ─────────────────────────────────── */
const REG: Record<string, { icon: LucideIcon; title: string }> = {
  web_search: { icon: Globe, title: 'Searched the web' },
  search_web: { icon: Globe, title: 'Searched the web' },
  x_search: { icon: Hash, title: 'Searched X' },
  md_quote: { icon: LineChart, title: 'Fetched live quotes' },
  md_history: { icon: Activity, title: 'Fetched price history' },
  md_technicals: { icon: Activity, title: 'Read technical signals' },
  md_fundamentals: { icon: Building2, title: 'Read fundamentals' },
  md_search: { icon: Search, title: 'Looked up a ticker' },
  data_fetch: { icon: Download, title: 'Fetched data to a file' },
  run_python: { icon: Code2, title: 'Ran Python' },
  run_bash: { icon: SquareTerminal, title: 'Ran a command' },
  list_files: { icon: FolderOpen, title: 'Listed sandbox files' },
  read_file: { icon: FileText, title: 'Read a file' },
  web_fetch: { icon: Link2, title: 'Fetched a page' },
  portfolio_buy: { icon: ArrowLeftRight, title: 'Recorded a trade' },
  portfolio_sell: { icon: ArrowLeftRight, title: 'Recorded a trade' },
  portfolio_set_position: { icon: Wallet, title: 'Updated the portfolio' },
  portfolio_remove_position: { icon: Wallet, title: 'Updated the portfolio' },
  portfolio_set_levels: { icon: Wallet, title: 'Set stop / target levels' },
  watch_create: { icon: Eye, title: 'Set a watcher' },
  scan_create: { icon: Radar, title: 'Set a market scan' },
  watch_update: { icon: Eye, title: 'Changed a watcher' },
  watch_delete: { icon: Eye, title: 'Removed a watcher' },
  watch_list: { icon: Eye, title: 'Listed watchers' },
  scan_run: { icon: Radar, title: 'Ran a scan' },
  schedule_create: { icon: CalendarClock, title: 'Scheduled' },
  schedule_update: { icon: CalendarClock, title: 'Changed a schedule' },
  schedule_delete: { icon: CalendarClock, title: 'Removed a schedule' },
  schedule_list: { icon: CalendarClock, title: 'Listed schedules' },
  wake_quiet: { icon: CalendarClock, title: 'Nothing new — logged quietly' },
  task_create: { icon: CalendarClock, title: 'Scheduled a task' },
  task_update: { icon: CalendarClock, title: 'Updated a task' },
  task_set_enabled: { icon: CalendarClock, title: 'Toggled a task' },
  task_delete: { icon: CalendarClock, title: 'Deleted a task' },
  task_list: { icon: CalendarClock, title: 'Listed tasks' },
  remember: { icon: BrainCog, title: 'Saved to memory' },
  forget: { icon: BrainCog, title: 'Removed from memory' },
  search_project: { icon: FolderSearch, title: 'Searched the project' },
  telegram_send: { icon: Send, title: 'Sent a Telegram message' },
  present_files: { icon: Download, title: 'Files ready to download' },
  worker_draft: { icon: Workflow, title: 'Drafted a trade worker' },
  worker_group_draft: { icon: Workflow, title: 'Drafted a worker group' },
  worker_list: { icon: Workflow, title: 'Looked up trade workers' },
  order_draft: { icon: Send, title: 'Drafted an order' },
  mcp: { icon: Database, title: 'Queried market data' },
  eodhd: { icon: Database, title: 'Queried market data' },
}
function metaFor(name: string): { icon: LucideIcon; title: string } {
  return REG[name] ?? { icon: Database, title: name }
}

function fmtDur(ms: number): string {
  const s = Math.max(0, Math.round(ms / 1000))
  return s < 60 ? `${s}s` : `${Math.floor(s / 60)}m ${s % 60}s`
}

/** Quiet, dense recap of what a set of tool events actually did — "5 searches ·
 *  3 pages · 2 code runs". Reads like a ledger line, not a widget. */
function kindRecap(events: ToolEvent[]): string {
  const buckets: [string, (n: string) => boolean][] = [
    ['search', (n) => n === 'web_search' || n === 'x_search' || n === 'search_web'],
    ['page', (n) => n === 'web_fetch'],
    ['data pull', (n) => n.startsWith('md_') || n === 'data_fetch' || n.startsWith('get_') || n.startsWith('mp_')],
    ['code run', (n) => n === 'run_python' || n === 'run_bash'],
    ['file read', (n) => n === 'read_file' || n === 'list_files'],
  ]
  const counts = buckets
    .map(([label, match]) => [label, events.filter((e) => match(e.name)).length] as const)
    .filter(([, c]) => c > 0)
  const named = counts.reduce((n, [, c]) => n + c, 0)
  const rest = events.length - named
  const parts = counts.slice(0, 3).map(([label, c]) => `${c} ${label}${c === 1 ? '' : 's'}`)
  if (rest > 0 && parts.length < 3) parts.push(`${rest} other`)
  return parts.join(' · ')
}

/** Exact billed cost, right-aligned and quiet ("$0.44" / "$0.022"). */
function fmtCost(usd: number): string {
  return `$${usd.toFixed(usd >= 0.1 ? 2 : 3)}`
}

/** Shared enter/exit motion for live rows: slide in, collapse away — GPU-friendly,
 *  and identical physics for every row so the stream reads as one calm mechanism. */
const rowMotion = {
  initial: { opacity: 0, y: 5, height: 0 as const },
  animate: { opacity: 1, y: 0, height: 'auto' as const },
  exit: { opacity: 0, y: -4, height: 0 as const },
  transition: { duration: 0.22, ease: [0.32, 0.72, 0, 1] as const },
}

/** Overlapping favicon stack + count for the sources a run actually read — the
 *  quiet "provenance" jewel on the card header. */
function FavStack({ cites }: { cites: Citation[] }) {
  const domains = [...new Set(cites.map((c) => domainOf(c.url)))]
  if (!domains.length) return null
  return (
    <span className="lb-act-favs" title={`${domains.length} sources`}>
      {domains.slice(0, 4).map((d) => (
        <img key={d} src={`https://icons.duckduckgo.com/ip3/${d}.ico`} alt="" loading="lazy"
          onError={(e) => { e.currentTarget.style.display = 'none' }} />
      ))}
      <span className="lb-act-favs-n">{domains.length} source{domains.length === 1 ? '' : 's'}</span>
    </span>
  )
}

/** What the run is doing RIGHT NOW, as a human sentence for the card header. */
function liveHeadline(di: DItem[], events: ToolEvent[]): string {
  const running = [...events].reverse().find((e) => e.status === 'running')
  if (running) {
    const { title } = metaFor(running.name)
    const det = detailFor(running)
    return det ? `${title} — ${det}` : title
  }
  const last = di[di.length - 1]
  if (last?.t === 'reason') return 'Thinking'
  return 'Working'
}

/** The specific second line under a tool's verb (query, filename, symbols…). */
function detailFor(e: ToolEvent): string | null {
  const out = e.output as Record<string, unknown> | undefined
  const inp = e.input as Record<string, unknown> | undefined
  if (e.name === 'run_bash') return (inp?.command as string) ?? null
  if (e.name === 'web_fetch') return (out?.title as string) || (out?.finalUrl as string) || (inp?.url as string) || null
  if (e.name === 'data_fetch') return (out?.file as string) ?? null
  if (e.name === 'read_file') return (inp?.path as string) ?? null
  // The server-set label is usually the most specific thing we have.
  if (e.label && e.label !== metaFor(e.name).title) return e.label
  return null
}

/** Expandable preview body for a tool (code, command, fetched excerpt). */
function previewFor(e: ToolEvent): { lang?: string; text: string } | null {
  const out = e.output as Record<string, unknown> | undefined
  const inp = e.input as Record<string, unknown> | undefined
  if (e.name === 'run_python' && typeof inp?.code === 'string') return { lang: 'python', text: inp.code }
  if (e.name === 'run_bash' && typeof inp?.command === 'string') return { lang: 'bash', text: inp.command }
  if (e.name === 'web_fetch') {
    const t = (out?.extract as string) || (out?.markdown as string)
    if (typeof t === 'string' && t.trim()) return { text: t.slice(0, 2000) }
  }
  if ((e.name === 'run_python' || e.name === 'run_bash') && typeof out?.stdout === 'string' && out.stdout.trim()) {
    return { text: `$ output\n${(out.stdout as string).slice(0, 2000)}` }
  }
  return null
}

/* ── thinking row (live ticker + collapsible reasoning) ────────────── */
function ThinkingRow({
  reasoning, thinkingMs, active, startedAt,
}: { reasoning?: string; thinkingMs?: number; active: boolean; startedAt?: number }) {
  const [now, setNow] = useState(() => Date.now())
  const [open, setOpen] = useState(false)
  useEffect(() => {
    if (!active) return
    const t = setInterval(() => setNow(Date.now()), 1000)
    return () => clearInterval(t)
  }, [active])

  const ms = active && startedAt ? now - startedAt : thinkingMs ?? 0
  if (!active && !reasoning && ms < 1500) return null // trivial, no trace → hide
  const canExpand = !!reasoning?.trim() && !active

  return (
    <div className="lb-tl-item">
      <button
        type="button"
        disabled={!canExpand}
        onClick={() => canExpand && setOpen((o) => !o)}
        className={cn('lb-tl-row', canExpand && 'lb-tl-row--btn')}
      >
        <span className="lb-tl-ico">
          {active ? <Sparkles size={14} className="animate-pulse" /> : <Sparkles size={14} />}
        </span>
        <span className="lb-tl-title">{active ? 'Thinking' : 'Thought'}</span>
        {(active || ms > 0) && <span className="lb-tl-dur">· {fmtDur(ms)}</span>}
        {canExpand && <ChevronRight size={13} className={cn('lb-tl-chev', open && 'lb-tl-chev--open')} />}
      </button>
      {open && reasoning && (
        <div className="lb-tl-reason">
          <Markdown content={reasoning} />
        </div>
      )}
    </div>
  )
}

/* ── one tool row ──────────────────────────────────────────────────── */
function ToolRow({ event, onClick }: { event: ToolEvent; onClick?: () => void }) {
  const [open, setOpen] = useState(false)
  const { icon: Icon, title } = metaFor(event.name)
  const detail = detailFor(event)
  const preview = previewFor(event)
  const running = event.status === 'running'
  const errored = event.status === 'error'
  const via = (event.output as { via?: string })?.via
  const interactive = !!preview || !!onClick

  return (
    <div className="lb-tl-item">
      <button
        type="button"
        disabled={!interactive}
        onClick={() => (preview ? setOpen((o) => !o) : onClick?.())}
        className={cn('lb-tl-row', interactive && 'lb-tl-row--btn')}
      >
        <span className={cn('lb-tl-ico', errored && 'text-down')}>
          <Icon size={14} />
        </span>
        <span className="lb-tl-title">{title}</span>
        {event.agentRole && <span className="lb-tl-badge" title={`Sub-agent: ${event.agentRole}`}>{event.agentRole}</span>}
        {detail && <span className="lb-tl-detail">{detail}</span>}
        {via === 'browser' && <span className="lb-tl-badge" title="Fetched via your browser (residential IP)">browser</span>}
        <span className="lb-tl-status">
          {running ? <Loader2 size={12} className="animate-spin" /> : errored ? <XIcon size={12} className="text-down" /> : <Check size={12} className="text-up" />}
        </span>
        {preview && <ChevronRight size={13} className={cn('lb-tl-chev', open && 'lb-tl-chev--open')} />}
      </button>
      {open && preview && (
        <pre className="lb-tl-pre">
          {preview.text}
        </pre>
      )}
      {event.name === 'present_files' && <PresentedFiles output={event.output} />}
    </div>
  )
}

/** Download links for files the AI delivered via present_files. */
function PresentedFiles({ output }: { output: unknown }) {
  const o = output as { files?: { name: string; title?: string; size?: number; url?: string; error?: string }[]; telegram?: string } | undefined
  const files = o?.files ?? []
  const ready = files.filter((f) => f.url)
  if (!ready.length && !o?.telegram) return null
  const fmtKB = (n?: number) => (n == null ? '' : n < 1024 ? `${n} B` : n < 1048576 ? `${Math.round(n / 1024)} KB` : `${(n / 1048576).toFixed(1)} MB`)
  return (
    <div className="lb-tl-files">
      {ready.map((f) => (
        <a key={f.url} href={f.url} download={f.name} className="lb-tl-file" target="_blank" rel="noreferrer">
          <Download size={13} className="lb-tl-file-ico" />
          <span className="lb-tl-file-name">{f.title || f.name}</span>
          {f.size != null && <span className="lb-tl-file-size">{fmtKB(f.size)}</span>}
        </a>
      ))}
      {o?.telegram && <span className="lb-tl-file-tg"><Send size={11} /> {o.telegram}</span>}
    </div>
  )
}

/* ── interactive calculator card (kept from the old ToolEvents) ─────── */
function CalcCard({ event }: { event: ToolEvent }) {
  const pushToCalculator = useStore((s) => s.pushToCalculator)
  const out = event.output as { expression?: string; result?: string } | undefined
  const expr = out?.expression ?? (event.input as { expression?: string })?.expression ?? ''
  const result = out?.result
  return (
    <button
      onClick={() => expr && pushToCalculator(expr)}
      className="card group flex w-full items-center gap-3 px-3.5 py-2 text-left transition-colors hover:border-[var(--line)]"
      title="Open in calculator"
    >
      <Calculator size={14} className="shrink-0 text-fg-subtle" />
      <div className="num min-w-0 flex-1 truncate text-[12.5px] text-fg-muted">{expr || '—'}</div>
      {result != null && <div className="num shrink-0 text-[13.5px] font-semibold text-fg">= {result}</div>}
    </button>
  )
}

const GROUPED = new Set(['web_search', 'x_search'])

/** Collapse repeated web_search / x_search events into one row each (Claude-style). */
type TLItem = { type: 'single'; event: ToolEvent } | { type: 'group'; name: string; events: ToolEvent[] }
function buildItems(rows: ToolEvent[]): TLItem[] {
  const out: TLItem[] = []
  const at: Record<string, number> = {}
  for (const e of rows) {
    if (GROUPED.has(e.name)) {
      if (at[e.name] === undefined) {
        at[e.name] = out.length
        out.push({ type: 'group', name: e.name, events: [e] })
      } else {
        ;(out[at[e.name]] as { events: ToolEvent[] }).events.push(e)
      }
    } else {
      out.push({ type: 'single', event: e })
    }
  }
  return out
}

function SearchGroupRow({ name, events, results }: { name: string; events: ToolEvent[]; results: Citation[] }) {
  const [open, setOpen] = useState(false)
  const { icon: Icon, title } = metaFor(name)
  const running = events.some((e) => e.status === 'running')
  const queries = [...new Set(events.map((e) => (e.label || '').trim()).filter(Boolean))]
  // Prefer showing how many RESULTS were found; fall back to query count.
  const detail = results.length ? `${results.length} results` : queries.length === 1 ? queries[0] : `${queries.length} searches`
  const expandable = results.length > 0 || queries.length > 1

  return (
    <div className="lb-tl-item">
      <button type="button" disabled={!expandable} onClick={() => expandable && setOpen((o) => !o)} className={cn('lb-tl-row', expandable && 'lb-tl-row--btn')}>
        <span className="lb-tl-ico"><Icon size={14} /></span>
        <span className="lb-tl-title">{title}</span>
        <span className="lb-tl-detail">{detail}</span>
        <span className="lb-tl-status">{running ? <Loader2 size={12} className="animate-spin" /> : <Check size={12} className="text-up" />}</span>
        {expandable && <ChevronRight size={13} className={cn('lb-tl-chev', open && 'lb-tl-chev--open')} />}
      </button>
      {open && expandable && (
        <div className="lb-tl-srp">
          {queries.length > 1 && (
            <div className="lb-tl-srp-q">{queries.map((q, i) => <span key={i} className="lb-tl-srp-chip">{q}</span>)}</div>
          )}
          {results.map((c, i) => (
            <a key={`${c.url}-${i}`} href={c.url} target="_blank" rel="noreferrer noopener" className="lb-tl-result">
              <img
                src={`https://icons.duckduckgo.com/ip3/${domainOf(c.url)}.ico`}
                alt=""
                className="lb-tl-fav"
                loading="lazy"
                onError={(e) => { e.currentTarget.style.visibility = 'hidden' }}
              />
              <span className="min-w-0 flex-1">
                <span className="lb-tl-result-title">{citeTitle(c)}</span>
                <span className="lb-tl-result-url">{domainOf(c.url)}</span>
                {c.snippet && <span className="lb-tl-result-snip">{c.snippet}</span>}
              </span>
            </a>
          ))}
        </div>
      )}
    </div>
  )
}

/* ── interleaved display item, built by walking `segments` in order ───── */
type DItem =
  | { t: 'reason'; text: string; active: boolean; key: string }
  | { t: 'group'; name: string; events: ToolEvent[]; key: string }
  | { t: 'tool'; event: ToolEvent; key: string }
  | { t: 'calc'; event: ToolEvent; key: string }
  | { t: 'lane'; agentId: string; role?: string; rows: ToolEvent[]; key: string }

function buildInterleaved(
  segments: NonNullable<ChatMessage['segments']>,
  events: ToolEvent[],
  streaming: boolean,
  hasContent: boolean,
): DItem[] {
  const byId = new Map(events.map((e) => [e.id, e]))
  const out: DItem[] = []
  // Heavy mode runs several agents in PARALLEL, so their events interleave in the stream —
  // group them BY agentId into one labeled lane each (not one jumbled block).
  const laneByAgent = new Map<string, Extract<DItem, { t: 'lane' }>>()
  let ri = 0
  for (const seg of segments) {
    if (seg.kind === 'reasoning') {
      out.push({ t: 'reason', text: seg.text, active: false, key: `r-${ri++}` })
      continue
    }
    const ev = byId.get(seg.eventId)
    if (!ev) continue
    // Sub-agent / Heavy-agent activity → its own per-agent lane (header = persona name).
    if (ev.agentId && (ev.depth ?? 0) >= 1) {
      let lane = laneByAgent.get(ev.agentId)
      if (!lane) {
        lane = { t: 'lane', agentId: ev.agentId, role: ev.agentRole, rows: [], key: `lane-${ev.agentId}` }
        laneByAgent.set(ev.agentId, lane)
        out.push(lane)
      }
      if (ev.agentRole && !lane.role) lane.role = ev.agentRole
      lane.rows.push(ev)
      continue
    }
    if (ev.name === 'calculate') {
      out.push({ t: 'calc', event: ev, key: ev.id })
    } else if (GROUPED.has(ev.name)) {
      const last = out[out.length - 1]
      if (last && last.t === 'group' && last.name === ev.name) last.events.push(ev)
      else out.push({ t: 'group', name: ev.name, events: [ev], key: `g-${ev.id}` })
    } else {
      out.push({ t: 'tool', event: ev, key: ev.id })
    }
  }
  // Live "Thinking" indicator at the tail while the turn is still working on its answer.
  if (streaming && !hasContent) {
    const last = out[out.length - 1]
    if (last && last.t === 'reason') last.active = true
    else out.push({ t: 'reason', text: '', active: true, key: 'r-active' })
  }
  return out
}

/** How many of the newest activity items stay visible while the turn is LIVE —
 *  older ones fold into a one-line "N earlier steps" stack (modern-agent style). */
const LIVE_TAIL = 2

export function ActivityTimeline({ message, streaming }: { message: ChatMessage; streaming?: boolean }) {
  const events = message.toolEvents ?? []
  const reasoning = message.meta?.reasoning
  const thinkingMs = message.meta?.thinkingMs
  const startedAt = (message.meta as { startedAt?: number } | undefined)?.startedAt
  const toggleMemory = useStore((s) => s.toggleMemory)
  // Live view: older steps folded (false) ↔ full history (true). Finished view:
  // whole timeline collapsed to a summary row (false) ↔ expanded (true).
  const [expanded, setExpanded] = useState(false)

  const thinkingActive = !!streaming && !message.content
  // Split the message's citations so each search group shows its own found results.
  const cites = message.citations ?? []
  const resultsFor = (name: string): Citation[] =>
    name === 'x_search' ? cites.filter((c) => c.source === 'x') : cites.filter((c) => c.source !== 'x')
  const onMem = (e: ToolEvent) => (e.name === 'remember' || e.name === 'forget' ? () => toggleMemory(true) : undefined)

  const renderItem = (it: DItem, live = false) =>
    it.t === 'reason' ? (
      <div key={it.key} className={cn(live && 'lb-tl-live')}>
        <ThinkingRow reasoning={it.text || undefined} thinkingMs={it.active ? undefined : 0} active={it.active} startedAt={startedAt} />
      </div>
    ) : it.t === 'group' ? (
      <div key={it.key} className={cn(live && 'lb-tl-live')}>
        <SearchGroupRow name={it.name} events={it.events} results={resultsFor(it.name)} />
      </div>
    ) : it.t === 'calc' ? (
      <CalcCard key={it.key} event={it.event} />
    ) : it.t === 'lane' ? (
      <div key={it.key} className={cn('my-1 overflow-hidden rounded-lg border border-[var(--line-soft)] bg-[var(--color-surface-2)]/40', live && 'lb-tl-live')}>
        <div className="flex items-center gap-1.5 px-2 py-1 text-[11px] font-medium text-fg-muted">
          <Sparkles size={12} className="shrink-0 text-fg-subtle" />
          {it.role || 'Agent'}
          <span className="text-fg-faint">· {it.rows.length} step{it.rows.length === 1 ? '' : 's'}</span>
        </div>
        <div className="space-y-1 border-t border-[var(--line-soft)] px-1.5 py-1">
          {it.rows.map((ev) => (
            <ToolRow key={ev.id} event={ev} onClick={onMem(ev)} />
          ))}
        </div>
      </div>
    ) : (
      <div key={it.key} className={cn(live && 'lb-tl-live')}>
        <ToolRow event={it.event} onClick={onMem(it.event)} />
      </div>
    )

  // ── Interleaved path: one designed ACTIVITY CARD — hairline shell, a header that
  // narrates the run (live: shimmering current action · counts; finished: duration ·
  // steps · source favicons · exact cost), and a vertical progress rail of steps.
  if (message.segments && message.segments.length) {
    const di = buildInterleaved(message.segments, events, !!streaming, !!message.content)
    if (!di.length) return null

    const stepCount = events.length
    const cost = message.meta?.usage?.costUsd
    const durMs = message.meta?.durationMs ?? thinkingMs ?? 0
    const presented = events.filter((e) => e.name === 'present_files')
    const calcs = events.filter((e) => e.name === 'calculate')

    // Tool-less turn → keep the lightweight "Thought for Ns" row, no card shell.
    if (!streaming && !stepCount) {
      return (
        <div className="lb-timeline">
          <ThinkingRow reasoning={reasoning} thinkingMs={thinkingMs} active={false} startedAt={startedAt} />
        </div>
      )
    }

    const foldCount = streaming && !expanded ? Math.max(0, di.length - LIVE_TAIL) : 0
    const visible = expanded || !streaming ? di : di.slice(foldCount)
    const hidden = di.slice(0, foldCount)
    const hiddenEvents = hidden.flatMap((x) => (x.t === 'group' ? x.events : x.t === 'lane' ? x.rows : x.t === 'tool' || x.t === 'calc' ? [x.event] : []))
    const showBody = streaming || expanded

    return (
      <div className="lb-timeline">
        <div className={cn('lb-act', streaming && 'lb-act--live')}>
          <button
            type="button"
            className="lb-act-hd"
            onClick={() => setExpanded((o) => !o)}
            title={streaming ? (expanded ? 'Fold earlier steps' : 'Show all steps') : expanded ? 'Collapse' : 'Show the full activity'}
          >
            <span className="lb-act-glyph">
              {streaming ? <Loader2 size={13} className="animate-spin" /> : <Sparkles size={13} />}
            </span>
            {streaming ? (
              <>
                <span className="lb-act-title lb-shimmer">{liveHeadline(di, events)}</span>
                <span className="lb-act-meta">{stepCount > 0 ? `${stepCount} step${stepCount === 1 ? '' : 's'}` : ''}</span>
              </>
            ) : (
              <>
                <span className="lb-act-title">{durMs >= 1000 ? `Worked for ${fmtDur(durMs)}` : 'Worked'}</span>
                <span className="lb-act-meta">{stepCount} step{stepCount === 1 ? '' : 's'}{kindRecap(events) ? ` · ${kindRecap(events)}` : ''}</span>
              </>
            )}
            <span className="lb-act-right">
              {!streaming && <FavStack cites={cites} />}
              {!streaming && cost != null && <span className="lb-act-cost num">{fmtCost(cost)}</span>}
              <ChevronRight size={13} className={cn('lb-tl-chev', expanded && 'lb-tl-chev--open')} />
            </span>
          </button>
          {showBody && (
            <div className="lb-act-body">
              {streaming && !expanded && hiddenEvents.length > 0 && (
                <button type="button" className="lb-act-fold" onClick={(e) => { e.stopPropagation(); setExpanded(true) }}>
                  <span className="lb-act-dot"><Layers size={11} /></span>
                  <span>{hiddenEvents.length} earlier step{hiddenEvents.length === 1 ? '' : 's'}</span>
                  <span className="lb-act-fold-recap">{kindRecap(hiddenEvents)}</span>
                </button>
              )}
              <AnimatePresence initial={false}>
                {visible.map((it, i) => (
                  <motion.div key={it.key} {...(streaming ? rowMotion : {})} style={streaming ? { overflow: 'hidden' } : undefined} className="lb-act-step">
                    {renderItem(it, !!streaming && i === visible.length - 1)}
                  </motion.div>
                ))}
              </AnimatePresence>
            </div>
          )}
        </div>
        {presented.map((e) => (
          <PresentedFiles key={e.id} output={e.output} />
        ))}
        {!showBody && calcs.map((e) => (
          <CalcCard key={e.id} event={e} />
        ))}
      </div>
    )
  }

  // ── Legacy path (older messages with no segments): thinking row, then grouped tools. ──
  const calcs = events.filter((e) => e.name === 'calculate')
  const items = buildItems(events.filter((e) => e.name !== 'calculate'))
  const showThinking = thinkingActive || !!reasoning?.trim() || (thinkingMs ?? 0) >= 1500
  if (!showThinking && !items.length && !calcs.length) return null

  return (
    <div className="lb-timeline">
      {showThinking && (
        <ThinkingRow reasoning={reasoning} thinkingMs={thinkingMs} active={thinkingActive} startedAt={startedAt} />
      )}
      {items.map((it, i) =>
        it.type === 'group' ? (
          <SearchGroupRow key={`g-${it.name}-${i}`} name={it.name} events={it.events} results={resultsFor(it.name)} />
        ) : (
          <ToolRow key={it.event.id} event={it.event} onClick={onMem(it.event)} />
        ),
      )}
      {calcs.length > 0 && (
        <div className="mt-1 space-y-1.5">
          {calcs.map((e) => (
            <CalcCard key={e.id} event={e} />
          ))}
        </div>
      )}
    </div>
  )
}
